import React, { useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { CPInput } from "./CPInput";
import { TaxLineEditor } from "./shared/TaxLineEditor";
import {
  formatInvoiceStatus,
  formatInvoiceUpdatedAt,
  parseInvoiceTaxLine,
} from "../helpers/invoices";
import { getInvoiceSummaryActions } from "../helpers/invoiceWorkflow";
import { resolveInvoiceSummaryBuckets } from "../helpers/invoiceReview";
import {
  formatMoneyFromCents,
  formatSettlementLabel,
  summarizeInvoiceSettlement,
} from "../helpers/invoiceMoney";
import { getInvoiceCollectionStatus } from "../helpers/invoiceDueStatus";
import { formatCurrency } from "../helpers/estimateTotals";
import {
  computeEstimateTaxSummary,
  getTaxLineLabel,
} from "../helpers/estimateTax";
import { getInvoiceStatusPillStyle } from "../helpers/invoiceStatusUi";
import {
  getInvoiceLineTypeMeta,
  INVOICE_LINE_TYPE,
} from "../helpers/invoiceLineTypes";
import {
  formatPaymentTermsLabel,
  resolveInvoiceDate,
  resolveInvoiceDueDate,
  buildPaymentTermsPresentation,
} from "../helpers/invoicePaymentTerms";
import { buildPaymentInstructionsPresentation } from "../helpers/invoicePaymentInstructions";
import { buildCustomerNotesPresentation } from "../helpers/invoiceCustomerNotes";
import { buildInvoiceFooterPresentation } from "../helpers/invoiceFooter";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { InvoiceSummaryDesktop } from "./invoice/InvoiceSummaryDesktop";
import { InvoicePaymentsSection } from "./invoice/InvoicePaymentsSection";
import { RecordPaymentModal } from "./accounts-receivable/RecordPaymentModal";
import "./materials/materialRecord.css";
import "./InvoiceSummaryScreen.css";
import {
  CP_MODAL_OVERLAY_STYLE,
  CP_MODAL_PANEL_STYLE,
} from "./shared/crewpilotModalStyles";
import { ViewportPortal } from "./shared/ViewportDialog";

const CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "16px",
  marginBottom: "12px",
};

const SECTION_TITLE_STYLE = {
  fontWeight: 700,
  fontSize: "14px",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  opacity: 0.78,
  marginBottom: "12px",
};

const ACTION_BTN = {
  padding: "10px 16px",
  borderRadius: "10px",
  border: "1px solid rgba(201,168,76,0.45)",
  background: "rgba(201,168,76,0.12)",
  color: "#c9a84c",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const ARCHIVE_BTN_STYLE = {
  ...ACTION_BTN,
  width: "100%",
  border: "1px solid rgba(224,107,90,0.45)",
  background: "rgba(224,107,90,0.12)",
  color: "#e06b5a",
};

const MODAL_OVERLAY_STYLE = CP_MODAL_OVERLAY_STYLE;
const MODAL_PANEL_STYLE = CP_MODAL_PANEL_STYLE;

function SummaryLine({ label, value, emphasis = false, indent = false }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: emphasis ? "10px" : "8px",
        marginLeft: indent ? "16px" : 0,
        fontSize: emphasis ? "15px" : "14px",
        fontWeight: emphasis ? 700 : 500,
        color: emphasis ? "#c9a84c" : "inherit",
        opacity: indent ? 0.9 : 1,
      }}
    >
      <span>{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}

function MetaField({ label, children }) {
  return (
    <div>
      <div className="invoice-summary__meta-label">{label}</div>
      <div className="invoice-summary__meta-value">{children}</div>
    </div>
  );
}

function buildLineItemSummary(lineItems = []) {
  const active = (lineItems || []).filter(
    (line) => (line.line_status || "active") === "active",
  );

  const counts = {
    labour: 0,
    material: 0,
    service: 0,
    other: 0,
  };

  for (const line of active) {
    if (line.line_type === INVOICE_LINE_TYPE.LABOUR) counts.labour += 1;
    else if (line.line_type === INVOICE_LINE_TYPE.MATERIAL) counts.material += 1;
    else if (line.line_type === INVOICE_LINE_TYPE.SERVICE) counts.service += 1;
    else counts.other += 1;
  }

  return { active, counts, total: active.length };
}

export function InvoiceSummaryScreen({
  invoice,
  saving,
  errorMessage,
  onBack,
  onSaveDraft,
  onPreviewInvoice,
  onExportPdf,
  onMarkSent,
  onMarkPaid,
  onReversePayment,
  onReopenToDraft,
  onArchiveInvoice,
}) {
  const isDesktop = useIsDesktopViewport();
  const settlement = summarizeInvoiceSettlement(invoice);
  const issued = ["sent", "paid", "archived"].includes(invoice?.status);
  const overdue =
    getInvoiceCollectionStatus(invoice) === "overdue" &&
    settlement.outstandingCents > 0;
  const actions = getInvoiceSummaryActions(invoice?.status, {
    hasUnreversedPayment: settlement.hasUnreversedPayment,
    outstandingCents: Array.isArray(invoice?.invoice_payments)
      ? settlement.outstandingCents
      : undefined,
  });
  const statusLabel = issued
    ? formatSettlementLabel(settlement.state, overdue)
    : formatInvoiceStatus(invoice?.status);
  const pillStyle = getInvoiceStatusPillStyle(invoice?.status);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [clientReference, setClientReference] = useState("");
  const [taxLine1, setTaxLine1] = useState(() => parseInvoiceTaxLine(null, 1));
  const [taxLine2, setTaxLine2] = useState(() => parseInvoiceTaxLine(null, 2));
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [showReverseConfirm, setShowReverseConfirm] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [mobilePane, setMobilePane] = useState("summary");
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    setInvoiceNumber(invoice.invoice_number || "");
    setNotes(invoice.notes || "");
    setClientReference(invoice.client_reference || "");
    setTaxLine1(parseInvoiceTaxLine(invoice, 1));
    setTaxLine2(parseInvoiceTaxLine(invoice, 2));
  }, [invoice]);

  const isFrozen = !actions.editTax;

  const buckets = useMemo(() => resolveInvoiceSummaryBuckets(invoice), [invoice]);
  const lineSubtotal = buckets.lineSubtotal;

  const taxSummary = useMemo(() => {
    if (isFrozen) {
      return {
        lines: [taxLine1, taxLine2]
          .filter((line) => line.type && line.type !== "none")
          .map((line) => {
            const computed = computeEstimateTaxSummary(
              Number(invoice?.subtotal || 0),
              [line],
            );
            return computed.lines[0] || null;
          })
          .filter(Boolean),
        totalTax: Number(invoice?.total_tax || 0),
        grandTotal: Number(invoice?.total_amount || 0),
      };
    }

    return computeEstimateTaxSummary(
      Number(invoice?.subtotal || lineSubtotal),
      [taxLine1, taxLine2],
    );
  }, [invoice, isFrozen, lineSubtotal, taxLine1, taxLine2]);

  const sourceEstimateNumber =
    invoice?.source_estimate_number ||
    invoice?.estimates?.estimate_number ||
    null;

  const lineSummary = useMemo(
    () => buildLineItemSummary(invoice?.lineItems),
    [invoice?.lineItems],
  );

  const paymentInstructionsPresentation = useMemo(
    () =>
      buildPaymentInstructionsPresentation(invoice?.payment_instructions),
    [invoice?.payment_instructions],
  );

  const paymentTermsPresentation = useMemo(
    () => buildPaymentTermsPresentation(invoice),
    [invoice],
  );

  const customerNotesPresentation = useMemo(
    () => buildCustomerNotesPresentation(invoice?.notes),
    [invoice?.notes],
  );

  const invoiceFooterPresentation = useMemo(
    () => buildInvoiceFooterPresentation(invoice?.invoice_footer),
    [invoice?.invoice_footer],
  );

  const showServices = buckets.services > 0 || lineSummary.counts.service > 0;
  const showOther = buckets.other > 0 || lineSummary.counts.other > 0;

  async function handleSaveDraft() {
    await onSaveDraft?.({
      invoiceNumber,
      notes,
      clientReference,
      taxLines: [taxLine1, taxLine2],
    });
  }

  async function handleConfirmArchive() {
    setArchiving(true);
    try {
      await onArchiveInvoice?.();
      setShowArchiveConfirm(false);
    } finally {
      setArchiving(false);
    }
  }

  if (!invoice) {
    return (
      <CrewPilotModuleShell
        title="Invoice Summary"
        sectionLabel="INVOICES"
        onBack={onBack}
        desktopPage
      >
        <p style={{ opacity: 0.7 }}>Invoice not found.</p>
      </CrewPilotModuleShell>
    );
  }

  const modals = (
    <>
    {showReverseConfirm && (
      <ViewportPortal>
      <div style={MODAL_OVERLAY_STYLE}>
        <div style={MODAL_PANEL_STYLE}>
          <h3 style={{ marginTop: 0 }}>Reverse Payment?</h3>
          <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
            This keeps the original receipt and records a reversal. Finance
            receives the reversing cash movement for this payment only. The
            invoice can return to Draft only when no valid payment remains.
          </p>
          <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
            <button
              type="button"
              className="nx-btn"
              style={{ flex: 1 }}
              onClick={() => setShowReverseConfirm(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="nx-btn nx-btn-gold"
              style={{ flex: 1 }}
              disabled={saving}
              onClick={async () => {
                try {
                  await onReversePayment?.(reverseTarget?.payment_id);
                  setShowReverseConfirm(false);
                  setReverseTarget(null);
                } catch {
                  // Parent handlers surface errors.
                }
              }}
            >
              {saving ? "Reversing…" : "Reverse Payment"}
            </button>
          </div>
        </div>
      </div>
      </ViewportPortal>
    )}

    {showReopenConfirm && (
      <ViewportPortal>
      <div style={MODAL_OVERLAY_STYLE}>
        <div style={MODAL_PANEL_STYLE}>
          <h3 style={{ marginTop: 0 }}>Reopen to Draft?</h3>
          <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
            This unlocks project labour and material allocations and returns
            the invoice to an editable Draft. The same invoice is kept — no
            duplicate is created. Paid invoices cannot be reopened this way.
          </p>
          <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
            <button
              type="button"
              className="nx-btn"
              style={{ flex: 1 }}
              onClick={() => setShowReopenConfirm(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="nx-btn nx-btn-gold"
              style={{ flex: 1 }}
              disabled={saving}
              onClick={async () => {
                try {
                  await onReopenToDraft?.();
                  setShowReopenConfirm(false);
                } catch {
                  // Parent handlers surface errors.
                }
              }}
            >
              {saving ? "Reopening…" : "Reopen to Draft"}
            </button>
          </div>
        </div>
      </div>
      </ViewportPortal>
    )}

    {showSendConfirm && (
      <ViewportPortal>
      <div style={MODAL_OVERLAY_STYLE}>
        <div style={MODAL_PANEL_STYLE}>
          <h3 style={{ marginTop: 0 }}>Send Invoice?</h3>
          <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
            Sending the invoice locks project allocations and marks the invoice
            as Sent.
          </p>
          <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
            <button
              type="button"
              className="nx-btn"
              style={{ flex: 1 }}
              onClick={() => setShowSendConfirm(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="nx-btn nx-btn-gold"
              style={{ flex: 1 }}
              disabled={saving}
              onClick={async () => {
                try {
                  await handleSaveDraft();
                  await onMarkSent?.();
                  setShowSendConfirm(false);
                } catch {
                  // Parent handlers surface errors.
                }
              }}
            >
              {saving ? "Sending…" : "Send Invoice"}
            </button>
          </div>
        </div>
      </div>
      </ViewportPortal>
    )}

    {showArchiveConfirm && (
      <ViewportPortal>
      <div style={MODAL_OVERLAY_STYLE}>
        <div style={MODAL_PANEL_STYLE}>
          <h3 style={{ marginTop: 0 }}>Archive Invoice?</h3>
          <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
            This invoice will move to the Archived filter and remain available
            for reference. Sent and paid invoices can be archived when no
            longer active.
          </p>
          <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
            <button
              type="button"
              className="nx-btn"
              style={{ flex: 1 }}
              onClick={() => setShowArchiveConfirm(false)}
              disabled={archiving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="nx-btn"
              style={{
                flex: 1,
                borderColor: "rgba(224,107,90,0.45)",
                color: "#e06b5a",
              }}
              onClick={handleConfirmArchive}
              disabled={archiving}
            >
              {archiving ? "Archiving…" : "Archive"}
            </button>
          </div>
        </div>
      </div>
      </ViewportPortal>
    )}
    {showRecordPayment && (
      <RecordPaymentModal
        invoice={invoice}
        saving={saving}
        onCancel={() => setShowRecordPayment(false)}
        onSubmit={async (details) => {
          await onMarkPaid?.(details);
          setShowRecordPayment(false);
        }}
      />
    )}
    </>
  );

  function requestReverse(payment) {
    setReverseTarget(payment);
    setShowReverseConfirm(true);
  }

  if (isDesktop) {
    return (
      <InvoiceSummaryDesktop
        invoice={invoice}
        actions={actions}
        saving={saving}
        errorMessage={errorMessage}
        onBack={onBack}
        fields={{
          invoiceNumber,
          setInvoiceNumber,
          notes,
          setNotes,
          clientReference,
          setClientReference,
          taxLine1,
          setTaxLine1,
          taxLine2,
          setTaxLine2,
        }}
        derived={{
          buckets,
          lineSubtotal,
          taxSummary,
          lineSummary,
          sourceEstimateNumber,
          showServices,
          showOther,
          paymentInstructionsPresentation,
          paymentTermsPresentation,
          customerNotesPresentation,
          invoiceFooterPresentation,
        }}
        handlers={{
          onSaveDraft: handleSaveDraft,
          onPreviewInvoice,
          onExportPdf,
          onSend: () => setShowSendConfirm(true),
          onMarkPaid: () => setShowRecordPayment(true),
          onReverse: requestReverse,
          onReopen: () => setShowReopenConfirm(true),
          onArchive: () => setShowArchiveConfirm(true),
        }}
      >
        {modals}
      </InvoiceSummaryDesktop>
    );
  }

  return (
    <CrewPilotModuleShell
      title={invoice.title || "Invoice Summary"}
      subtitle={invoice.invoice_number || "Invoice"}
      sectionLabel="INVOICE"
      onBack={onBack}
      desktopPage
      compact
    >
      <div className="invoice-summary">
        {errorMessage ? (
          <p
            role="alert"
            style={{
              margin: "0 0 12px",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid rgba(224,107,90,0.45)",
              background: "rgba(224,107,90,0.12)",
              color: "#e06b5a",
              fontSize: "13px",
              lineHeight: 1.45,
            }}
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="invoice-summary__hero">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
            <div
              style={{
                ...pillStyle,
                borderRadius: "999px",
                padding: "6px 12px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {statusLabel}
            </div>
          </div>
          <div className="invoice-summary__tiles">
            <div className="invoice-summary__tile">
              <span>Total</span>
              <strong>{formatMoneyFromCents(settlement.totalCents)}</strong>
            </div>
            <div className="invoice-summary__tile">
              <span>Paid</span>
              <strong>{formatMoneyFromCents(settlement.paidCents)}</strong>
            </div>
            <div className="invoice-summary__tile is-balance">
              <span>Balance</span>
              <strong>{formatMoneyFromCents(settlement.outstandingCents)}</strong>
            </div>
          </div>
        </div>

        <div className="invoice-summary__tabs" role="tablist" aria-label="Invoice sections">
          {[
            ["summary", "Summary"],
            ["details", "Details"],
            ["payments", "Payments"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mobilePane === value}
              onClick={() => setMobilePane(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {mobilePane === "summary" ? (
          <>
            <div style={CARD_STYLE}>
              <div style={SECTION_TITLE_STYLE}>Client & Project</div>
              {actions.editInvoiceNumber ? (
                <CPInput
                  label="Invoice Number"
                  value={invoiceNumber}
                  onChange={setInvoiceNumber}
                  placeholder="INV-..."
                />
              ) : null}
              <div className="invoice-summary__meta-grid">
                <MetaField label="Client">{invoice.clients?.client_name || "—"}</MetaField>
                <MetaField label="Project">{invoice.title || "—"}</MetaField>
                <MetaField label="Invoice Date">
                  {formatInvoiceUpdatedAt(resolveInvoiceDate(invoice) || invoice.invoice_date)}
                </MetaField>
                <MetaField label="Due Date">
                  {formatInvoiceUpdatedAt(resolveInvoiceDueDate(invoice) || invoice.due_date)}
                </MetaField>
                <MetaField label="Payment Terms">{formatPaymentTermsLabel(invoice)}</MetaField>
              </div>
            </div>

            <div style={CARD_STYLE}>
              <div style={SECTION_TITLE_STYLE}>Line Items</div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px 16px",
                  marginBottom: "12px",
                  fontSize: "13px",
                  opacity: 0.78,
                }}
              >
                <span>{lineSummary.total} line{lineSummary.total === 1 ? "" : "s"}</span>
                {lineSummary.counts.labour > 0 && <span>Labour · {lineSummary.counts.labour}</span>}
                {lineSummary.counts.material > 0 && <span>Materials · {lineSummary.counts.material}</span>}
                {lineSummary.counts.service > 0 && <span>Services · {lineSummary.counts.service}</span>}
                {lineSummary.counts.other > 0 && <span>Other · {lineSummary.counts.other}</span>}
              </div>
              {lineSummary.active.length === 0 ? (
                <p style={{ margin: 0, opacity: 0.55, fontSize: "13px" }}>
                  No active line items on this invoice.
                </p>
              ) : (
                lineSummary.active.slice(0, 12).map((line) => {
                  const meta = getInvoiceLineTypeMeta(line.line_type);
                  return (
                    <div key={line.line_id} className="invoice-summary__line-row">
                      <div className="invoice-summary__line-desc">
                        <div className="invoice-summary__line-type">
                          {meta.label || line.line_type || "Line"}
                        </div>
                        <div>{line.description || line.item_name || "Untitled line"}</div>
                        {line.detail ? (
                          <div className="invoice-summary__line-detail">{line.detail}</div>
                        ) : null}
                      </div>
                      <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {formatCurrency(Number(line.line_total || 0))}
                      </div>
                    </div>
                  );
                })
              )}
              {lineSummary.active.length > 12 ? (
                <p style={{ margin: "10px 0 0", fontSize: "12px", opacity: 0.55 }}>
                  +{lineSummary.active.length - 12} more lines — use Preview for the full invoice.
                </p>
              ) : null}
            </div>

            <div style={CARD_STYLE} className="invoice-summary__totals-card">
              <div style={SECTION_TITLE_STYLE}>Totals</div>
              <SummaryLine label="Labour" value={buckets.labour} />
              <SummaryLine label="Materials" value={buckets.materials} />
              {showServices && <SummaryLine label="Services" value={buckets.services} />}
              {showOther && <SummaryLine label="Other" value={buckets.other} />}
              <SummaryLine label="Subtotal" value={lineSubtotal} />
              {Number(invoice.project_discount_percent || 0) > 0 && (
                <SummaryLine
                  label={`Project discount (${invoice.project_discount_percent}%)`}
                  value={lineSubtotal * (1 - Number(invoice.project_discount_percent) / 100)}
                  indent
                />
              )}
              {Number(invoice.goodwill_adjustment || 0) !== 0 && (
                <SummaryLine
                  label="Goodwill adjustment"
                  value={Number(invoice.goodwill_adjustment || 0)}
                  indent
                />
              )}
              <SummaryLine label="Taxable subtotal" value={Number(invoice.subtotal || lineSubtotal)} />
              {taxSummary.lines.map((line) => (
                <SummaryLine
                  key={`${line.type}-${line.label}`}
                  label={`${getTaxLineLabel(line)} (${line.rate}%)`}
                  value={line.amount}
                  indent
                />
              ))}
              <SummaryLine label="Grand Total" value={taxSummary.grandTotal} emphasis />
            </div>

            <div className="invoice-summary__primary-actions">
              {actions.editTax && (
                <button
                  type="button"
                  className="nx-btn nx-btn-gold"
                  style={ACTION_BTN}
                  disabled={saving}
                  onClick={handleSaveDraft}
                >
                  {saving ? "Saving…" : "Save Draft"}
                </button>
              )}
              {actions.markSent && (
                <button
                  type="button"
                  className="nx-btn nx-btn-gold"
                  style={ACTION_BTN}
                  disabled={saving}
                  onClick={() => setShowSendConfirm(true)}
                >
                  Send Invoice
                </button>
              )}
              {actions.previewInvoice && (
                <button type="button" className="nx-btn" style={ACTION_BTN} onClick={onPreviewInvoice}>
                  Preview Invoice
                </button>
              )}
              {actions.markPaid && (
                <button
                  type="button"
                  className="rec-btn rec-btn--primary"
                  disabled={saving}
                  onClick={() => setShowRecordPayment(true)}
                >
                  Record Payment
                </button>
              )}
            </div>
          </>
        ) : null}

        {mobilePane === "details" ? (
          <>
            <div style={CARD_STYLE}>
              <div style={SECTION_TITLE_STYLE}>Record</div>
              <div className="invoice-summary__meta-grid">
                <MetaField label="Deposit Account">
                  {invoice.deposit_account?.name || invoice.deposit_account_id || "—"}
                </MetaField>
                {sourceEstimateNumber ? (
                  <MetaField label="Source Estimate">{sourceEstimateNumber}</MetaField>
                ) : null}
                <MetaField label="Created">
                  {formatInvoiceUpdatedAt(invoice.created_at || invoice.seeded_at)}
                </MetaField>
                <MetaField label="Updated">{formatInvoiceUpdatedAt(invoice.updated_at)}</MetaField>
                {invoice.sent_at ? (
                  <MetaField label="Sent">{formatInvoiceUpdatedAt(invoice.sent_at)}</MetaField>
                ) : null}
                {invoice.paid_at ? (
                  <MetaField label="Paid">{formatInvoiceUpdatedAt(invoice.paid_at)}</MetaField>
                ) : null}
              </div>
            </div>

            <div style={CARD_STYLE}>
              <div style={SECTION_TITLE_STYLE}>Client Reference</div>
              {actions.editClientReference ? (
                <CPInput
                  label=""
                  value={clientReference}
                  onChange={setClientReference}
                  placeholder="PO / work order (optional)"
                  disabled={saving}
                />
              ) : (
                <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.5 }}>
                  {clientReference?.trim() || "—"}
                </p>
              )}
            </div>

            {paymentInstructionsPresentation ? (
              <div style={CARD_STYLE}>
                <div style={SECTION_TITLE_STYLE}>How to Pay</div>
                {paymentInstructionsPresentation.blocks.map((block, index) => {
                  if (block.type === "field") {
                    return (
                      <div key={`pi-field-${index}`} style={{ marginBottom: "10px" }}>
                        <div style={{ fontSize: "12px", opacity: 0.6, marginBottom: "4px" }}>{block.label}</div>
                        <div style={{ fontSize: "14px", fontWeight: 600 }}>{block.value}</div>
                      </div>
                    );
                  }
                  if (block.type === "text") {
                    return (
                      <p
                        key={`pi-text-${index}`}
                        style={{ margin: "0 0 8px", fontSize: "13px", lineHeight: 1.45, opacity: 0.85, whiteSpace: "pre-wrap" }}
                      >
                        {block.text}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            ) : null}

            {paymentTermsPresentation ? (
              <div style={CARD_STYLE}>
                <div style={SECTION_TITLE_STYLE}>Payment Terms</div>
                {paymentTermsPresentation.blocks.map((block, index) => {
                  if (block.type === "field") {
                    return (
                      <div key={`pt-field-${index}`} style={{ marginBottom: "10px" }}>
                        <div style={{ fontSize: "12px", opacity: 0.6, marginBottom: "4px" }}>{block.label}</div>
                        <div style={{ fontSize: "14px", fontWeight: 600 }}>{block.value}</div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            ) : null}

            {(customerNotesPresentation || actions.editNotes) ? (
              <div style={CARD_STYLE}>
                <div style={SECTION_TITLE_STYLE}>Customer Notes</div>
                {actions.editNotes ? (
                  <CPInput
                    label=""
                    value={notes}
                    onChange={setNotes}
                    placeholder="Customer notes (optional)"
                    disabled={saving}
                  />
                ) : (
                  <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {notes?.trim() || "—"}
                  </p>
                )}
              </div>
            ) : null}

            {invoiceFooterPresentation ? (
              <div style={CARD_STYLE}>
                <div style={SECTION_TITLE_STYLE}>Invoice Footer</div>
                <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.45, opacity: 0.75, whiteSpace: "pre-wrap" }}>
                  {invoiceFooterPresentation.text}
                </p>
              </div>
            ) : null}

            {actions.editTax && (
              <div style={CARD_STYLE}>
                <div style={SECTION_TITLE_STYLE}>Tax</div>
                <TaxLineEditor lineNumber={1} line={taxLine1} onChange={setTaxLine1} disabled={saving} />
                <TaxLineEditor lineNumber={2} line={taxLine2} onChange={setTaxLine2} disabled={saving} />
              </div>
            )}

            <div className="invoice-summary__aside-actions">
              <p className="invoice-summary__workflow-hint">
                This is the management view. To change line items or amounts, reopen the invoice to Draft and edit in Invoice Review.
              </p>
              {actions.exportPdf && (
                <button type="button" className="nx-btn" style={ACTION_BTN} onClick={onExportPdf}>
                  Export PDF
                </button>
              )}
              {actions.reopenToDraft && (
                <button
                  type="button"
                  className="nx-btn"
                  style={ACTION_BTN}
                  disabled={saving}
                  onClick={() => setShowReopenConfirm(true)}
                >
                  Reopen to Draft
                </button>
              )}
              {actions.archive && (
                <button type="button" style={ARCHIVE_BTN_STYLE} onClick={() => setShowArchiveConfirm(true)}>
                  Archive Invoice
                </button>
              )}
            </div>
          </>
        ) : null}

        {mobilePane === "payments" ? (
          <>
            <InvoicePaymentsSection
              invoice={invoice}
              overdue={overdue}
              canReverse={Boolean(onReversePayment)}
              saving={saving}
              onReverse={requestReverse}
            />
            {actions.markPaid ? (
              <div className="invoice-summary__primary-actions" style={{ marginTop: "12px" }}>
                <button
                  type="button"
                  className="rec-btn rec-btn--primary"
                  disabled={saving}
                  onClick={() => setShowRecordPayment(true)}
                >
                  Record Payment
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {modals}
    </CrewPilotModuleShell>
  );
}
